import { useEffect, useRef } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  useLoggedIn,
  LastLoginCookieData,
  getLastLoginLocalStorage,
  updateLastLoginLocalStorage,
  useLastLogin,
  LAST_LOGIN_COOKIE,
} from '@moonpig/web-core-auth'
import {
  CookieCategory,
  getBrowserCookie,
  setBrowserCookie,
  destroyBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import { logger } from '@moonpig/web-core-monitoring'
import { queryUserEmail } from './queryUserEmail'
import { inferLoginMethod } from './inferLoginMethod'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

const parseCookie = (cookieValue: string): LastLoginCookieData | null => {
  try {
    const parsed = JSON.parse(cookieValue) as LastLoginCookieData
    if (parsed.email && parsed.method) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

const hashEmail = async (email: string): Promise<string> => {
  const subtleCrypto = globalThis.crypto?.subtle
  if (subtleCrypto) {
    const hashBuffer = await subtleCrypto.digest(
      'SHA-256',
      new TextEncoder().encode(email.toLowerCase()),
    )
    return Array.from(new Uint8Array(hashBuffer))
      .map(value => value.toString(16).padStart(2, '0'))
      .join('')
  }

  const cryptoModule = await import('crypto')
  return cryptoModule
    .createHash('sha256')
    .update(email.toLowerCase())
    .digest('hex')
}

export const CreateLastLoginCookie = () => {
  const client = useApolloClient()
  const { loggedIn } = useLoggedIn()
  const lastLoginContext = useLastLogin()
  const hasProcessedRef = useRef(false)
  const lastLoginContextRef = useRef(lastLoginContext)
  lastLoginContextRef.current = lastLoginContext

  useEffect(() => {
    const createOrUpdateCookie = async () => {
      try {
        const context = lastLoginContextRef.current

        const existingCookieValue = getBrowserCookie(LAST_LOGIN_COOKIE)

        const existingCookie = existingCookieValue
          ? parseCookie(existingCookieValue)
          : null

        if (loggedIn) {
          const localStorage = getLastLoginLocalStorage()

          const email = await queryUserEmail(client.query)

          if (!email) {
            context?.setInitialized()
            return
          }

          const currentHashedEmail = await hashEmail(email)

          if (
            localStorage?.hashedEmail &&
            localStorage?.hashedEmail !== currentHashedEmail
          ) {
            destroyBrowserCookie(LAST_LOGIN_COOKIE, { path: '/' })
          }

          const method = inferLoginMethod(email)

          const cookieData: LastLoginCookieData = { email, method }

          setBrowserCookie(
            LAST_LOGIN_COOKIE,
            JSON.stringify(cookieData),
            {
              path: '/',
              maxAge: COOKIE_MAX_AGE,
              secure: true,
              sameSite: 'strict',
            },
            CookieCategory.STRICTLY_NECESSARY,
          )

          if (context) {
            context.setLastLogin(cookieData)
          }

          updateLastLoginLocalStorage(method, currentHashedEmail)

          hasProcessedRef.current = true
          context?.setInitialized()
        } else {
          if (existingCookie && context) {
            context.setLastLogin(existingCookie)
          }

          hasProcessedRef.current = false
          context?.setInitialized()
        }
      } catch (e) {
        logger.warning('Could not create last login cookie', {}, e)
        lastLoginContextRef.current?.setInitialized()
      }
    }

    createOrUpdateCookie()
  }, [client.query, loggedIn])

  return null
}
